import { syncLinkedItemZonesFromTextures } from '@context/utils/linked/syncLinkedItemZonesFromTextures'
import { findZoneKeyForTexture } from '@context/utils/linked/syncLinkedItemZonesFromTextures'
import getStandardMaterial from '@utils/conversion/getStandardMaterial'
import isLinked from '@utils/sceneObjects/assertions/isLinked'
import {
  applyMaterialToZoneMaterial,
  pickTextureEntryFields,
} from '@utils/sceneObjects/linked/linkedTextureFields'
import {
  AcabadoZoneEntry,
  fetchZoneAcabadoInfo,
  hasZoneAcabadoInfoCache,
  resolveTextureCatalogMatch,
} from '@utils/sceneObjects/linked/resolveTextureCatalogMatch'
import {
  CatalogTextureRecord,
  normalizeTextureUrl,
  StoredTextureLike,
} from '@utils/sceneObjects/linked/textureCatalogApi'

export type { CatalogTextureRecord } from '@utils/sceneObjects/linked/textureCatalogApi'
export {
  clearCatalogTexturesCache,
  fetchCatalogTexturesForAcabado,
  findMatchingCatalogTexture,
} from '@utils/sceneObjects/linked/textureCatalogApi'

type StoredTexture = StoredTextureLike & {
  img?: string
  complemento?: string
  color?: string
  repeatX?: number
  repeatY?: number
  opacity?: number
  roughness?: number
  metalness?: number
  envMapIntensity?: number
  bumpScale?: number
  mirror?: boolean
  bumpMap?: string | null
  displacement?: number
  displacementScale?: number | null
  textureRotation?: number
}

export type TextureReconciliationResult = {
  fixedCount: number
  checkedCount: number
  unresolvedCount: number
  apiCalls: number
  fixedItemIds: string[]
  unresolved: Array<{
    itemId: string
    catalogID?: string
    zoneName?: string
    zoneDisplayName?: string
    searchID?: number
    name?: string
    url?: string
  }>
}

function parseAcabadoUnidadId(texture: StoredTexture): number | null {
  if (typeof texture.linkedTextureID === 'number' && texture.linkedTextureID > 0) {
    return texture.linkedTextureID
  }

  const compositeId = texture.idAcabadoUnidadModeloAcabado
  if (!compositeId) return null

  const suffix = compositeId.split('_').pop()
  const parsed = Number(suffix)
  return Number.isFinite(parsed) && parsed > 0 ? parsed : null
}

function isTextureCatalogConsistent(
  applied: StoredTexture,
  catalogTexture: CatalogTextureRecord,
  meta?: { acabadoFamilia?: string; zoneSlotId?: number },
): boolean {
  const urlMismatch =
    Boolean(applied.url && catalogTexture.url) &&
    normalizeTextureUrl(applied.url!) !== normalizeTextureUrl(catalogTexture.url)

  return (
    applied.searchID === catalogTexture.idTexturas &&
    applied.id === catalogTexture.identificadorTextura &&
    applied.groupID === catalogTexture.idGruposDeTexturas &&
    !urlMismatch &&
    (!meta?.acabadoFamilia || applied.acabado === meta.acabadoFamilia) &&
    (!meta?.zoneSlotId || applied.linkedTextureID === meta.zoneSlotId)
  )
}

export function applyCatalogTextureFix(
  applied: StoredTexture,
  catalogTexture: CatalogTextureRecord,
  meta?: { acabadoFamilia?: string; zoneSlotId?: number },
): boolean {
  if (isTextureCatalogConsistent(applied, catalogTexture, meta)) return false

  const entryFields = pickTextureEntryFields({
    searchID: catalogTexture.idTexturas,
    id: catalogTexture.identificadorTextura,
    groupID: catalogTexture.idGruposDeTexturas,
    url: catalogTexture.url || applied.url,
    name: catalogTexture.nombre || applied.name,
    acabado: meta?.acabadoFamilia ?? applied.acabado,
    color: catalogTexture.color ?? applied.color,
    repeatX: catalogTexture.factorRepeticionX ?? applied.repeatX,
    repeatY: catalogTexture.factorRepeticionY ?? applied.repeatY,
    opacity: catalogTexture.opacidad ?? applied.opacity,
    roughness: catalogTexture.brillo ?? applied.roughness,
    metalness: catalogTexture.metal ?? applied.metalness,
    envMapIntensity: catalogTexture.reflejo ?? applied.envMapIntensity,
    bumpScale: catalogTexture.rugosidad ?? applied.bumpScale,
    mirror: catalogTexture.espejo ?? applied.mirror,
    bumpMap: catalogTexture.rugosidadFichero ?? applied.bumpMap,
    displacement: catalogTexture.desplazamiento ?? applied.displacement,
    displacementScale: catalogTexture.factorDeEscala ?? applied.displacementScale,
    textureRotation: catalogTexture.orientacionTextura ?? applied.textureRotation,
  })

  Object.assign(applied, entryFields)

  if (meta?.zoneSlotId) {
    applied.linkedTextureID = meta.zoneSlotId
  }

  return true
}

export async function reconcileProjectTexturesOnLoad(
  items: Map<string, unknown>,
): Promise<TextureReconciliationResult> {
  const result: TextureReconciliationResult = {
    fixedCount: 0,
    checkedCount: 0,
    unresolvedCount: 0,
    apiCalls: 0,
    fixedItemIds: [],
    unresolved: [],
  }

  try {
    return await reconcileProjectTexturesOnLoadInternal(items, result)
  } catch {
    return result
  }
}

async function reconcileProjectTexturesOnLoadInternal(
  items: Map<string, unknown>,
  result: TextureReconciliationResult,
): Promise<TextureReconciliationResult> {
  const catalogByAcabadoId = new Map<number, CatalogTextureRecord[]>()
  const zoneInfoByLinkedTextureId = new Map<number, AcabadoZoneEntry[]>()
  const linkedTextureIds = new Set<number>()

  for (const item of items.values()) {
    if (!isLinked(item) || !Array.isArray(item.textures)) continue

    for (const texture of item.textures as StoredTexture[]) {
      const linkedTextureID = parseAcabadoUnidadId(texture)
      if (linkedTextureID && linkedTextureID > 0) linkedTextureIds.add(linkedTextureID)
    }
  }

  await Promise.allSettled(
    Array.from(linkedTextureIds).map(async (linkedTextureID) => {
      try {
        const wasCached = hasZoneAcabadoInfoCache(linkedTextureID)
        const zoneList = await fetchZoneAcabadoInfo(linkedTextureID)
        zoneInfoByLinkedTextureId.set(linkedTextureID, zoneList)
        if (!wasCached) result.apiCalls += 1
      } catch {
        zoneInfoByLinkedTextureId.set(linkedTextureID, [])
      }
    }),
  )

  for (const [itemId, item] of items.entries()) {
    if (!isLinked(item) || !Array.isArray(item.textures) || item.textures.length === 0) continue

    let itemFixed = false

    for (const texture of item.textures as StoredTexture[]) {
      result.checkedCount += 1

      const linkedTextureID = parseAcabadoUnidadId(texture)
      const zoneList = linkedTextureID
        ? zoneInfoByLinkedTextureId.get(linkedTextureID)
        : undefined

      let matchResult = null
      try {
        matchResult = await resolveTextureCatalogMatch(texture, {
          idUnidades: typeof item.idUnidades === 'number' ? item.idUnidades : undefined,
          zoneList,
          catalogByAcabadoId,
          onApiCall: () => {
            result.apiCalls += 1
          },
        })
      } catch {
        // texture match failed — leave as unresolved
      }

      if (!matchResult) {
        result.unresolvedCount += 1
        result.unresolved.push({
          itemId,
          catalogID: String(item.catalogID ?? ''),
          zoneName: texture.zoneName,
          zoneDisplayName: texture.zoneDisplayName,
          searchID: texture.searchID,
          name: texture.name,
          url: texture.url,
        })
        continue
      }

      const { catalogTexture, acabadoFamilia, zoneSlotId } = matchResult
      const zoneKey = findZoneKeyForTexture(item.zones ?? {}, texture)

      if (zoneKey && item.zones?.[zoneKey]?.material) {
        const fullMaterial = getStandardMaterial(
          catalogTexture,
          acabadoFamilia ?? texture.acabado,
          texture.modelo,
        )
        item.zones[zoneKey].material = applyMaterialToZoneMaterial(
          item.zones[zoneKey].material as Record<string, unknown>,
          fullMaterial as Record<string, unknown>,
        )
      }

      if (
        applyCatalogTextureFix(texture, catalogTexture, {
          acabadoFamilia,
          zoneSlotId,
        })
      ) {
        result.fixedCount += 1
        itemFixed = true
      }
    }

    syncLinkedItemZonesFromTextures(item, {
      itemId,
      catalogID: String(item.catalogID ?? ''),
      source: 'projectLoad:reconcileTextures',
    })

    if (itemFixed) {
      result.fixedItemIds.push(itemId)
    }
  }

  return result
}
